import { Component, HostListener, Injector, ViewChild } from '@angular/core';
import { AlertController, IonMenu, LoadingController, MenuController, ModalController, NavController, Platform, PopoverController, ToastController } from '@ionic/angular';
import { AppSettings } from './core/configs/app.settings';
import { Entity } from './core/data/schema/entity';
import { ServiceHelper } from './core/helper/services.helper';
import { HttpService } from './core/data/service/http.service';
import { BrowserHelper } from './core/helper/browser.helper';
import { AuthenticationService } from './core/modules/auth-core/data/service/authentication.service';
import { Community } from './data/schema/community';
import { EntityFactory } from './core/data/schema/entity-factory';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { MultiSelectListComponent } from './shared/component/multi-select/multi-select-list/multi-select-list.component';
import { createCustomElement } from '@angular/elements';
import { CommunityService } from './data/service/community.service';
import { CommunitySubscription } from './data/schema/cbs/community-subscription';
import { Transaction, TransactionStatus } from './data/schema/ecommerce/transaction';
import { Purchase } from './data/schema/ecommerce/purchase';
import { Log } from './core/log';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {

  @ViewChild('ionMenu') ionMenu: IonMenu;

  isL57 = false;
  helpRoute: string;
  showSideMenu: boolean = false;
  usingCollapsingSideMenu: boolean = false;
  widerSideMenu: boolean = false;
  inNewCommunity: boolean = false;

  get currentCommunity() { return Community.activeCommunity; }

  get buildInfo() { return `${AppSettings.version} (${AppSettings.publishDate})` }

  constructor(
    private router: Router,
    private communityService: CommunityService,
    httpService: HttpService,
    authSvc: AuthenticationService,
    alertCtrl: AlertController,
    loadCtrl: LoadingController,
    navCtrl: NavController,
    platform: Platform,
    toastCtrl: ToastController,
    browserHelper: BrowserHelper,
    popoverCtrl: PopoverController,
    title: Title,
    menuCtrl: MenuController,
    sanitizer: DomSanitizer,
    modalCtrl: ModalController,
    injector: Injector
  ) {

    // disable initially, so it doesn't flicker on login and new community pages
    menuCtrl.enable(false);

    this.router.events.forEach(item => {
      if (item instanceof NavigationEnd) {
        if (item.url.includes('auth') || item.url == '/') {
          this.showSideMenu = false;
        } else if (item.url.includes('new-community')) {
          this.showSideMenu = authSvc.profile?.userId > 0;
        } else {
          this.showSideMenu = true;
        }
        this.inNewCommunity = item.url.includes('/new-community');
        this.updateHelpBubble(item);
        ServiceHelper.routeHistory.unshift(item.url);
        if (ServiceHelper.routeHistory.length > 3) {
          ServiceHelper.routeHistory.pop();
        }
      }
    });
    this.isL57 = authSvc.profile?.username?.endsWith('launch57.com');

    Entity.factory = new EntityFactory(httpService);
    //Entity.factory = new EntityFactoryLocal (httpService, appSettings);

    ServiceHelper.authSvc = authSvc;
    ServiceHelper.popoverCtrl = popoverCtrl;
    ServiceHelper.loadCtrl = loadCtrl;
    ServiceHelper.navCtrl = navCtrl;
    ServiceHelper.platform = platform;
    ServiceHelper.alertCtrl = alertCtrl;
    ServiceHelper.toastCtrl = toastCtrl;
    ServiceHelper.httpSvc = httpService;
    ServiceHelper.title = title;
    ServiceHelper.menuCtrl = menuCtrl;
    ServiceHelper.sanitizer = sanitizer;
    ServiceHelper.modalCtrl = modalCtrl;
    ServiceHelper.appComponent = this;

    // initialize the multi-select component
    const element = createCustomElement(MultiSelectListComponent, {
      injector,
    });
    customElements.define(`c-multi-select-list`, element);

    // on successful login, auto or regular, we need to call the server to fetch
    // the list of communities this user has access to as admin so we can include
    // in drop down in header, and on My Communities page
    authSvc.loginStateChanged.subscribe(async state => {
      if (state === 1 && !window.location.href.includes('new-community')) {
        Log.debug('login successful, fetch users communities and set one as active');
        CommunitySubscription.currentSubscription = null; // reset it since we are logging in

        const currCommunityId = +localStorage.getItem('curr_community');
        if (currCommunityId > 0) {
          await this.communityService.loadCommunity(currCommunityId);
        }


        // if at this point activeCommunity is null, then the user doesn't appear to have
        // a community created yet. like they open BB and immediately clicked a button to go 
        // to CBS. So in this case, lets popup a notice that they need to first create a community
        // before they can access any features
        if (Community.activeCommunity == null) {

          // first, lets check if we really don't have a community, could likely only
          // happen on first create of community and not refreshing bid beacon
          const myAdminCommunities = await Community.getMyCommunities(true)
          if (myAdminCommunities.length == 0) {
            Log.debug('AppComponent - user has no admin communities, take to new community wizard ');
            // take them to new community wizard and show a popup explaining
            await ServiceHelper.navCtrl.navigateRoot('/home/new-community');
            await ServiceHelper.displayAlert('Create Your Community',
              'Start by creating your community, then you can access the many features of CommuniBee!');
            return;
          } else {
            await this.communityService.loadCommunity(myAdminCommunities[0].communityId);
            localStorage.setItem('curr_community', myAdminCommunities[0].communityId.toString());
          }
        }

        // SSO might redirect somewhere else so lets check
        // depending on if we came from SSO or not, we may need to send users to a specific page
        const ssoPath = localStorage.getItem('ssoPath');

        if (String.isNullOrWhiteSpace(ssoPath)) {
          await ServiceHelper.navCtrl.navigateRoot('/engagement/dashboard');
        } else if (ssoPath === 'marketplace') {
          localStorage.removeItem('ssoPath');
          await ServiceHelper.navCtrl.navigateRoot('/marketplace/dash?src=create');
        } else if (ssoPath === 'create') {
          localStorage.removeItem('ssoPath');
          await ServiceHelper.navCtrl.navigateRoot('/fundraising/auctions?src=create');
        } else if (ssoPath === 'subscribe') {
          localStorage.removeItem('ssoPath');
          await ServiceHelper.navCtrl.navigateRoot('/community/subscription');
        } else if (ssoPath === 'billing') {
          localStorage.removeItem('ssoPath');
          await ServiceHelper.navCtrl.navigateRoot('/community/billing');
        } else if (ssoPath.includes('/')) {
          // if we have a slash, then we have a very specific target path
          localStorage.removeItem('ssoPath');
          await ServiceHelper.navCtrl.navigateRoot(ssoPath);
        } else {
          localStorage.removeItem('ssoPath');
          await ServiceHelper.navCtrl.navigateRoot(`/home/communities`);
        }
      }
    });

    // on logout, take user to login page
    authSvc.loginStateChanged.subscribe(state => {
      if (state === 0) {
        Log.debug('logout successful, take user to login page');
        ServiceHelper.navCtrl.navigateRoot('/auth/login');
      }
    });

    // handle any embedded links being clicked and open them in new tab or in app browser
    // this event is thrown from code in index.html
    window.addEventListener('linkclick', (e: any) => {
      Log.debug('linkclick event fired with: ', e)
      browserHelper.openUrl(e.detail);
    }, false);

    this.onResize();

    this.checkForTxCancel();
  }

  async checkForTxCancel() {
    // if coming back from stripe checkout using browser back, not back button,
    // this local storage item would not have been cleared, but means tx was canceled
    const redirUrl = localStorage.getItem('cancel_url');
    let url = window.location.href;

    // check if the url has a cancel param and a transactionId, if so, then we need to cancel the transaction
    if (url.includes('status=cancelled') || redirUrl != null) {
      try {

        if (redirUrl != null) {
          url = redirUrl;
          localStorage.removeItem('cancel_url');
        }

        // url = https://app.launch57.com/community/subscription?status=cancelled&transaction_id=12345
        // or https://app.launch57.com/community/subscription?status=cancelled&purchase_id=12345

        // Log up the transaction, if it's deferred, then don't cancel, otherwise go for it
        let txId = +(url.substring(url.indexOf('transaction_id=') + 15) ?? '0');
        if (txId === 0) {
          // check if we have a purchase id in the url set from window.location.href
          txId = +(url.substring(url.indexOf('purchase_id=') + 12) ?? '0');
        }

        if (txId > 0) {
          const tx = await Transaction.load(txId, false);
          if (tx?.deferred === false) {
            Purchase.cancelPurchase(txId);
          }
        } else if (redirUrl.includes('order-details') && window.location.href.includes('manual-order')) {
          ServiceHelper.navCtrl.navigateRoot(redirUrl.replace(AppSettings.baseUrl, ''));
        }

      } catch (e) {
        console.error(e);
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.usingCollapsingSideMenu = (window.innerWidth < 991);
    this.widerSideMenu = (window.innerWidth > 1249);
  }

  setClipboard() {
    navigator.clipboard.writeText(this.helpRoute);
  }

  clearFundraiser() {
    localStorage.removeItem('fundraiserid4create');
  }

  updateHelpBubble(item: any) {
    const pageKey = this.getUrl(item.url);
    // Log.debug('Page Key:', pageKey);
    this.helpRoute = pageKey;
  }

  getUrl(page: string) {
    let url = page.replace('/admin/', '').replace(/[0-9]/g, '');
    const slashIndex = url.indexOf('/');
    if (slashIndex > 0) {
      url = url.substring(slashIndex);
    }
    return url;
  }
}
